import { useFormikContext } from 'formik';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef } from 'react';

interface UseFormObserverProps<T> {
  onChange: (values: T) => void;
  debounceTime?: number;
}

export const useFormObserver = <T extends Record<string, any>>({
  onChange,
  debounceTime = 1000,
}: UseFormObserverProps<T>) => {
  const { values, status } = useFormikContext<T>();

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const debouncedOnChange = useMemo(
    () => debounce((newValues: T) => onChangeRef.current(newValues), debounceTime),
    [debounceTime],
  );

  useEffect(() => {
    if (status?.displayMode) return;

    debouncedOnChange(values);

    return () => {
      debouncedOnChange.cancel();
    };
  }, [values, debouncedOnChange]);
};

export const FormObserver = <T extends Record<string, any>>({
  onChange,
  debounceTime = 500,
}: UseFormObserverProps<T>) => {
  useFormObserver({ onChange, debounceTime });
  return null;
};
